import { ApiError } from './errors';
import { getAuthToken, getCaptureApiBaseUrl, getUnauthorizedHandler } from './config';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | undefined>;
}

function buildUrl(path: string, query?: Record<string, string | undefined>): string {
  const url = new URL(path.replace(/^\//, ''), `${getCaptureApiBaseUrl().replace(/\/$/, '')}/`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const { method = 'GET', body, query } = options;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  try {
    return await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    throw ApiError.network(cause instanceof Error ? cause.message : 'Network request failed');
  }
}

/** The one place every INTERFACES.md section 3 (and section 12 review)
 * route goes through. Attaches the Cognito bearer token, throws `ApiError`
 * built from the platform's shared envelope on any non-2xx response, and
 * throws a typed network `ApiError` when `fetch` itself fails (offline,
 * DNS, CORS) so callers never see a raw `TypeError`.
 *
 * A 401 on a call that carried a token gets exactly one recovery
 * attempt: the registered unauthorized
 * handler refreshes the Cognito session and the call is retried with the
 * new token. A second 401, or a handler that cannot refresh, throws
 * `Unauthorized` and leaves the sign-out to the auth provider. One retry,
 * never a loop. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getAuthToken();
  let response = await send(path, options, token);

  // Only a call that carried a token has a session to refresh. A 401 on a
  // call made before sign-in is the expected answer, and routing it to the
  // refresh handler ended a session that never existed and told a first-time
  // visitor their session had expired.
  if (response.status === 401 && token) {
    const handler = getUnauthorizedHandler();
    const refreshedToken = handler ? await handler() : null;
    if (refreshedToken) {
      response = await send(path, options, refreshedToken);
    }
  }

  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Uploads asset bytes directly to the presigned URL from
 * `POST /capture/upload-url`. Not a JSON call and not through `request`:
 * the presigned URL is not a capture-api route and carries no auth
 * header of ours. */
export async function uploadAssetBytes(uploadUrl: string, bytes: Blob, contentType: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: bytes,
    });
  } catch (cause) {
    throw ApiError.network(cause instanceof Error ? cause.message : 'Asset upload failed');
  }
  if (!response.ok) {
    throw ApiError.network(`Asset upload failed with status ${response.status}`);
  }
}
